import React, { useState } from 'react';

interface ProofOfPaymentViewerProps {
  isOpen: boolean;
  onClose: () => void;
  proofUrl: string;
  transactionHash?: string;
  amount: number;
  currency: string;
  withdrawalDate: string;
}

export const ProofOfPaymentViewer: React.FC<ProofOfPaymentViewerProps> = ({
  isOpen,
  onClose,
  proofUrl,
  transactionHash,
  amount,
  currency,
  withdrawalDate
}) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  if (!isOpen) return null;

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
      <div className="bg-gray-800 rounded-lg border border-gray-700 max-w-4xl w-full max-h-[90vh] overflow-auto mb-8">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white">📄 Proof of Payment</h2>
            <p className="text-white text-sm mt-1">
              Withdrawal of ${amount.toFixed(2)} {currency} • {new Date(withdrawalDate).toLocaleDateString()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors text-2xl"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Transaction Details */}
          {transactionHash && (
            <div className="bg-gray-700 rounded-lg p-4 mb-6">
              <h3 className="text-white font-medium mb-3">🔗 Transaction Details</h3>
              <div className="space-y-2">
                <div>
                  <span className="text-white text-sm">Transaction Hash:</span>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="bg-gray-800 p-2 rounded text-sm font-mono text-gray-300 break-all flex-1">
                      {transactionHash}
                    </div>
                    <button
                      onClick={() => copyToClipboard(transactionHash)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm transition-colors"
                      title="Copy to clipboard"
                    >
                      📋
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Proof Image/Document */}
          <div className="bg-gray-700 rounded-lg p-4">
            <h3 className="text-white font-medium mb-3">📸 Payment Proof Document</h3>
            
            {imageLoading && (
              <div className="flex items-center justify-center h-64 bg-gray-800 rounded">
                <div className="text-white">Loading proof document...</div>
              </div>
            )}

            {imageError ? (
              <div className="bg-red-900/20 border border-red-500/30 rounded p-4 text-center">
                <div className="text-red-400 mb-2">❌ Unable to load proof document</div>
                <a
                  href={proofUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors"
                >
                  🔗 Open in new tab →
                </a>
              </div>
            ) : (
              <div className="text-center">
                <img
                  src={proofUrl}
                  alt="Proof of Payment"
                  className={`max-w-full h-auto rounded border border-gray-600 ${imageLoading ? 'hidden' : ''}`}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
                
                <div className="mt-4 flex gap-3 justify-center">
                  <a
                    href={proofUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors text-sm"
                  >
                    🔗 Open in New Tab
                  </a>
                  <a
                    href={proofUrl}
                    download
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors text-sm"
                  >
                    💾 Download
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-blue-900/20 border border-blue-500/30 rounded p-4 mt-6">
            <h4 className="text-blue-400 font-medium mb-2">ℹ️ What to do next:</h4>
            <ul className="text-gray-300 text-sm space-y-1">
              <li>• Check your wallet for the received funds</li>
              <li>• Verify the transaction hash on the blockchain explorer</li>
              <li>• Contact support if you haven't received the funds within 24 hours</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
